import { useTranslate } from "@similarweb/i18n-provider";
import { SWReactCountryIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { deviceIconMap } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import type { LocationDevice } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import CountryService from "app/services/CountryService";

import type { GscKeyword, SavedGscMarket } from "../../types";

import { GoogleMarketActions } from "./GoogleMarketActions";
import { KeywordMetrics } from "./KeywordMetrics";
import { MarketContainer } from "./styles";

const DEVICE_LABEL: Record<LocationDevice, string> = {
    DESKTOP: "rankTracker.gsc.market.device.desktop",
    MOBILE: "rankTracker.gsc.market.device.mobile",
    BOTH: "rankTracker.gsc.market.device.allTraffic",
};

type Props = {
    savedMarket: SavedGscMarket;
    // We need all keywords from API to show matrics based on selected keywords vs. real data
    keywords: GscKeyword[];
    onEdit: (isEditing: boolean) => void;
    onDelete?: () => void;
};

export const GoogleMarketSaved = ({ savedMarket, keywords, onEdit, onDelete }: Props) => {
    const { translate } = useTranslate();

    const country = CountryService.getCountryByCode(savedMarket.countryCode);
    const countryName = country?.text ?? savedMarket.countryCode;
    const countryId = country?.id;
    const label = `${countryName} - ${savedMarket.language.text}`;

    const DeviceIcon = deviceIconMap[savedMarket.device];
    const deviceLabel = translate(DEVICE_LABEL[savedMarket.device]);

    return (
        <MarketContainer data-automation="gsc-market-saved">
            <Flex
                justifyContent="space-between"
                alignItems="center"
            >
                <Flex
                    alignItems="center"
                    gap="16px"
                >
                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        {countryId ? (
                            <SWReactCountryIcons
                                countryCode={countryId}
                                size="22px"
                            />
                        ) : null}
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon[400]}
                        >
                            {label}
                        </Text>
                    </Flex>
                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        <DeviceIcon
                            size="xs"
                            color={colorsPalettes.carbon[200]}
                        />
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon[400]}
                        >
                            {deviceLabel}
                        </Text>
                    </Flex>
                </Flex>
                <GoogleMarketActions
                    onEdit={onEdit}
                    onDelete={onDelete}
                />
            </Flex>

            <KeywordMetrics
                compact
                keywords={keywords}
                selectedCount={savedMarket.keywords.length}
                selectedDevice={savedMarket.device}
            />
        </MarketContainer>
    );
};
